import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Clock,
  CreditCard,
  IdCard,
  Loader2,
  ShieldCheck,
  Upload,
  X,
  type LucideIcon,
} from "lucide-react";
import { cloudinaryUpload } from "@/services/middleware/uploadImage";
import { getStudentProfileAPI } from "@/services/api/user/getStudentProfile";
import { UploadStudentDocsAPI } from "@/services/api/user/uploadStudentDocs";
import {
  getStudentDocumentStatus,
  getStudentVerificationBadge,
  getStudentVerificationState,
  getStudentVerificationSubtitle,
  type StudentVerificationBadgeKey,
  type StudentVerificationDocument,
  type StudentVerificationState,
} from "@/utils/studentVerification";

interface StudentVerificationCardProps {
  onUploaded?: () => void;
}

/** The header badge: one look per verification state. */
const BADGE: Record<
  StudentVerificationBadgeKey,
  { className: string; Icon: LucideIcon }
> = {
  Verified: {
    className:
      "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-2.5 py-1 text-xs font-bold text-green-600",
    Icon: Check,
  },
  Rejected: {
    className:
      "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600",
    Icon: X,
  },
  "Pending approval": {
    className:
      "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-600",
    Icon: Clock,
  },
  "Not Verified": {
    className:
      "inline-flex shrink-0 items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-500",
    Icon: ShieldCheck,
  },
};

/** The pill of a document that is already on file. */
const DOCUMENT_PILL = {
  verified: "bg-green-50 text-green-600",
  uploaded: "bg-amber-50 text-amber-600",
} as const;

const readFileAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

/**
 * Account-page counterpart of the sponsor redemption `uploadDocs` step. Stores
 * the HKID and/or student card image; the account only becomes "verified" once
 * an admin approves it, so the card re-reads the profile to show "Pending".
 *
 * Upload is per document and only offered when that document still needs it:
 * the document is missing, or an admin rejected it. A document that is on file
 * and under review — or that is already approved — just shows its status pill,
 * so a student can never replace a document the admin is looking at, and can
 * never replace an approved one. UploadStudentDocsAPI clears `rejected_at`, so
 * a re-upload flips the card straight back to "Pending approval".
 */
const StudentVerificationCard: React.FC<StudentVerificationCardProps> = ({
  onUploaded,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState<StudentVerificationState | null>(null);
  const [busyDoc, setBusyDoc] = useState<StudentVerificationDocument | null>(
    null,
  );
  const [error, setError] = useState("");
  const cardInputRef = useRef<HTMLInputElement>(null);
  const hkidInputRef = useRef<HTMLInputElement>(null);

  const loadStatus = async () => {
    try {
      const resp = await getStudentProfileAPI();
      setState(getStudentVerificationState(resp?.result));
    } catch {
      setState(getStudentVerificationState(null));
    }
  };

  useEffect(() => {
    loadStatus();
  }, []);

  /**
   * The "no re-upload once it is on file" rule, in one place.
   *
   * The state is derived per document (see `@/utils/studentVerification`), so
   * an approved student card and an HKID that is still under review are two
   * different facts: only the document that is still missing — or that an
   * admin rejected — may be uploaded again.
   */
  const needsUploadFor = (doc: StudentVerificationDocument) => {
    if (!state) return false;
    return doc === "hkid" ? state.hkidNeedsUpload : state.cardNeedsUpload;
  };

  /** Upload a single document straight from its row; the other one is untouched. */
  const handleDocumentPick = async (
    doc: StudentVerificationDocument,
    file: File,
  ) => {
    // The row only offers the upload button when this holds; the check is
    // repeated here so the rule cannot be broken by a stale render.
    if (!needsUploadFor(doc)) {
      setError(t("This document is already on file and cannot be replaced."));
      return;
    }
    setBusyDoc(doc);
    setError("");
    try {
      const url = await cloudinaryUpload(await readFileAsDataUrl(file));
      if (!url) {
        setError(t("Upload failed. Please try again."));
        return;
      }
      const resp = await UploadStudentDocsAPI(
        doc === "hkid" ? { hkidUri: url } : { studentCardUri: url },
      );
      if (resp?.status !== 0) {
        setError(
          resp?.resp || resp?.message || t("Upload failed. Please try again."),
        );
        return;
      }
      await loadStatus();
      onUploaded?.();
    } catch {
      setError(t("Upload failed. Please try again."));
    } finally {
      setBusyDoc(null);
    }
  };

  const badgeKey = state ? getStudentVerificationBadge(state) : null;
  const subtitle = state ? getStudentVerificationSubtitle(state) : null;

  const badge = (() => {
    if (!badgeKey) return null;
    const { className, Icon: BadgeIcon } = BADGE[badgeKey];
    return (
      <span className={className}>
        <BadgeIcon size={13} />
        {t(badgeKey)}
      </span>
    );
  })();

  /**
   * Per-document rows. Every status is derived per document: the admin
   * approves each document separately, so an approved student card and an HKID
   * that is still under review are two different facts and must not read as
   * one "Verified".
   */
  const documentRows = (
    [
      {
        key: "student_card" as const,
        label: t("Student Card"),
        hint: t(
          "Upload the front of your student card. Once approved, you can join activities and events.",
        ),
        cta: t("Upload Student Card"),
        Icon: CreditCard,
        inputRef: cardInputRef,
      },
      {
        key: "hkid" as const,
        label: t("HKID"),
        hint: t(
          "Upload the front of your Hong Kong Identity Card. Needed to redeem coupons and sponsor offers (with an approved student card).",
        ),
        cta: t("Upload HKID"),
        Icon: IdCard,
        inputRef: hkidInputRef,
      },
    ] as const
  ).map((doc) => {
    const RowIcon = doc.Icon;
    // The status decides between the upload button and the status pill; a
    // document that is on file and under review shows "Uploaded", an approved
    // one shows "Verified".
    const status = state
      ? getStudentDocumentStatus(state, doc.key)
      : "needs-upload";
    const busy = busyDoc === doc.key;
    const pill =
      status === "verified" ? DOCUMENT_PILL.verified : DOCUMENT_PILL.uploaded;
    const statusLabel = status === "verified" ? t("Verified") : t("Uploaded");
    return (
      <div
        key={doc.key}
        className="flex items-center gap-2.5 rounded-xl bg-gray-50 px-3 py-2"
      >
        <RowIcon
          size={15}
          className="mt-0.5 shrink-0 self-start text-gray-400"
        />
        <div className="min-w-0 flex-1">
          <span className="block text-xs font-semibold text-gray-700">
            {doc.label}
          </span>
          <span className="mt-0.5 block text-xs leading-4 text-gray-500">
            {doc.hint}
          </span>
        </div>
        {status === "needs-upload" ? (
          <>
            <button
              type="button"
              onClick={() => doc.inputRef.current?.click()}
              disabled={busyDoc !== null}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-[#ffa217] px-2.5 py-1 text-[11px] font-bold text-white transition hover:bg-[#e08f0e] disabled:opacity-60"
            >
              {busy ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <Upload size={12} />
              )}
              {busy ? t("Uploading...") : doc.cta}
            </button>
            <input
              ref={doc.inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                // Reset first so picking the same file again still fires onChange.
                event.target.value = "";
                if (!file) return;
                await handleDocumentPick(doc.key, file);
              }}
            />
          </>
        ) : (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${pill}`}
          >
            {statusLabel}
          </span>
        )}
      </div>
    );
  });

  return (
    <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#fff3e0]">
          <IdCard size={18} className="text-[#ffa217]" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-extrabold text-gray-900">
            {t("Student Verification")}
          </h2>
          {subtitle ? (
            <p
              className={`text-xs ${subtitle.notice ? "text-[#ffa217]" : "text-gray-500"}`}
            >
              {t(subtitle.key)}
            </p>
          ) : null}
        </div>
        {state === null ? (
          <Loader2 size={16} className="shrink-0 animate-spin text-[#ffa217]" />
        ) : (
          badge
        )}
      </div>

      {state !== null && (
        <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
          {documentRows}
          {error && (
            <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">
              {error}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default StudentVerificationCard;
